import React from 'react';
import { getAuction } from '../../../../models/auctions';
import { getLoggedUser } from '../../../../state/user';
import i18n from '../../../../i18n';
import { Link, NavLink } from 'react-router-dom';
import logoThawani from '../../../../logoThawani.svg';
import { getUserInfo } from '../../../../models/users';
import { VisitNow } from './../../../home/helpers/visitNow';
import { checkBlackList } from '../../../../models/blacklist';
import HeaderBanner from './../../../common/HeaderBanner';
import {
  CreditCard,
  Building2,
  Wallet,
  Upload,
  FileText,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  CheckCircle,
  ShieldAlert,
  Ban,
  Eye,
  Info,
  Loader,
  Layers,
  Check,
  Plus,
  Loader2,
  X,
  ScanLine,
  Copy,
  Download,
} from 'lucide-react';
import { OmrRiyal, OMR } from './../../../common/icons';
import { formatCurrency } from '../../../../state/utils';
import '../../../../styles/components/enroll-page.scss';
import { toast } from 'react-toastify';
import { fetchAcknowledgementPdf, scanReceipt, userEnrollment } from './services/enrollService';
import { openBlobInNewTab } from '../../../../utils/common';
import { CustomModal } from '../../../common/CustomModal';
import { convertPdfToImage } from '../../../../utils/pdfToImage';
import { withRouter } from '../../../../hoc/withRouter';
import { initiateOnlineEnroll } from '../../../dashboard/pages/Payment/services/paymentService';
import SmartPayRedirectForm from '../../../dashboard/pages/Payment/SmartPayRedirectForm';

// ─────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────

const loggedInUser = getLoggedUser();

const PROFILE_FIELD_LABELS = {
  file_id_number: i18n.t('attach_id_no'),
  authority_name: i18n.t('auth_name'),
  bank: i18n.t('bank'),
  account_number: i18n.t('account_number'),
  cr_number: i18n.t('cr_number'),
  resident_card_number: i18n.t('personal_card_no'),
  vat_number: i18n.t('vat_number'),
  file_cr_number: i18n.t('attacth_cr_copy'),
  file_auth_letter: i18n.t('auth_letter'),
  file_vat_certificate: i18n.t('attach_vat'),
};

const OFFLINE_BANK_FIELDS = [
  {
    name: 'transfer_date',
    label: i18n.t('transfer_date') || 'Transfer Date',
    placeholder: '',
    type: 'date',
  },
  {
    name: 'receipt_number',
    label: i18n.t('receipt'),
    placeholder: i18n.t('please_enter_receipt_number'),
    type: 'text',
  },
  {
    name: 'bank',
    label: i18n.t('bank'),
    placeholder: i18n.t('please_enter_bank_name'),
    type: 'text',
  },
  {
    name: 'account_number',
    label: i18n.t('account_number'),
    placeholder: i18n.t('please_enter_bank_account'),
    type: 'text',
  },
  {
    name: 'beneficiary',
    label: i18n.t('beneficiary'),
    placeholder: i18n.t('please_enter_beneficiary_name'),
    type: 'text',
  },
];

const BANK_DETAIL_FIELDS = [
  { key: 'bank_name', label: i18n.t('bank_name') || 'Bank Name' },
  { key: 'account_name', label: i18n.t('account_name') || 'Account Name' },
  { key: 'account_number', label: i18n.t('account_number') || 'Account Number' },
  { key: 'iban', label: 'IBAN' },
  { key: 'swift_code', label: i18n.t('swift_code') || 'SWIFT Code' },
  { key: 'currency', label: i18n.t('currency') || 'Currency' },
  { key: 'branch_name', label: i18n.t('branch') || 'Branch' },
];

const ONLINE_POLICY_POINTS = [
  'Deposit a Guarantee amount to participate.',
  'Refundable. Service charges: 1.5% local, 2% credit (non-refundable).',
  'Separate from item value.',
  'May vary per item.',
  'Not automatically returned.',
  'Returns to wallet for refund request.',
];

const MAX_FILE_SIZE_KB = 5120;
const PRICE_AMOUNT_MULTIPLIER = 1000;

// Fixed, non-refundable documentation & administration fee charged on every
// ONLINE enrollment payment, in addition to the auction's enroll amount.
// Confirm this value / field names against the backend contract before going live.
const ONLINE_PROCESSING_FEE = 10;

// ─────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────

function getAuctionIdFromPath() {
  const path = window.location.pathname;
  return path.substring(path.lastIndexOf('/') + 1);
}

function isFileSizeValid(file) {
  return file.size / 1024 <= MAX_FILE_SIZE_KB;
}

function resolveDefaultPaymentState(groupInfo) {
  let paymentType = '';
  let requiresOnlinePayment = false;
  let showPaymentDetails = false;

  if (!groupInfo) return { paymentType, requiresOnlinePayment, showPaymentDetails };

  if (groupInfo.can_offline) {
    paymentType = 'offline';
  } else if (groupInfo.can_wallet) {
    paymentType = 'wallet';
  } else if (groupInfo.can_online) {
    paymentType = 'online';
    requiresOnlinePayment = true;
    showPaymentDetails = true;
  }

  return { paymentType, requiresOnlinePayment, showPaymentDetails };
}

// ─────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────

class AuctionEnrollment extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // UI toggles
      showTermsPanel: false,
      showBankPanel: true,
      showPaymentDetails: false,

      // Payment
      paymentType: '',
      selectedCardType: 'local',
      requiresOnlinePayment: false,
      selectedPriceUnits: 20000,

      // Form inputs
      formInput: { ...loggedInUser },
      fieldErrors: {},
      isCompany: loggedInUser.is_company || 0,
      termsAccepted: false,
      receiptFile: null,

      // Auction
      auctionId: getAuctionIdFromPath(),
      auctionDetails: {},
      termsContent: '',

      // User / profile
      walletBalance: 0,
      isProfileComplete: true,
      missingProfileFields: [],
      isBlacklisted: false,

      // Enroll flow
      enrollData: [],
      thawaniCustomerId: '',
      isEnrolled: false,
      showSuccessMessage: false,
      rulesAccepted: false,

      // Loading states
      isPageLoading: false,
      isSubmitting: false,
      isPaymentRedirecting: false,

      // Receipt Upload
      receiptPreviewUrl: null,
      receiptFileError: null,
      isDraggingOver: false,

      // Receipt scanning
      isScanning: false,
      scanError: null,

      // Acknowledge
      showAcknowledgementModal: false,

      copiedField: null,
      isGeneratingImage: false,

      // Online payment — Terms & Conditions / fee disclosure acceptance
      showOnlineTermsModal: false,
      onlineTermsUnlocked: false, // becomes true once the user taps "I Agree" in the modal
      onlineTermsAccepted: false, // reflects the checkbox state
    };

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleEnrollSubmit = this.handleEnrollSubmit.bind(this);
  }

  // ─── Lifecycle ─────────────────────────────────────────────

  componentDidMount() {
    window.scrollTo(0, 0);
    this.loadAuctionDetails();
    this.loadUserWalletInfo();
    this.loadBlacklistStatus();
  }

  componentWillUnmount() {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
  }

  // ─── Data Loaders ──────────────────────────────────────────

  async loadAuctionDetails() {
    this.setState({ isPageLoading: true });

    const response = await getAuction(this.state.auctionId);
    if (!response) {
      this.setState({ isPageLoading: false });
      return;
    }

    const auction = response.data;
    const { paymentType, requiresOnlinePayment, showPaymentDetails } = resolveDefaultPaymentState(
      auction.group_info
    );

    const isOnlyOnline = auction.payment_type === 'Online';
    const acceptsOnlinePayment =
      auction.payment_type === 'Online' || auction.payment_type === 'Both';

    this.setState({
      auctionDetails: auction,
      termsContent: auction.terms,
      paymentType,
      requiresOnlinePayment: isOnlyOnline ? true : requiresOnlinePayment,
      showPaymentDetails: isOnlyOnline ? true : showPaymentDetails,
      selectedPriceUnits:
        acceptsOnlinePayment && auction.payment_amount
          ? auction.payment_amount * PRICE_AMOUNT_MULTIPLIER
          : this.state.selectedPriceUnits,
      isPageLoading: false,
    });
  }

  async loadUserWalletInfo() {
    const response = await getUserInfo(loggedInUser.id);
    if (!response?.success) return;

    const { data: userData, meta } = response;
    const profile = meta?.profile;

    loggedInUser.wallet_amount = userData.wallet_amount;
    localStorage.setItem('mzad_user_info', JSON.stringify(loggedInUser));

    const hasInsufficientBalance =
      Number(userData.wallet_amount) === 0 ||
      Number(userData.wallet_amount) < Number(this.state.auctionDetails.payment_amount);

    this.setState({
      walletBalance: userData.wallet_amount,
      isProfileComplete: !profile?.update_required,
      missingProfileFields: profile?.missing_fields ?? [],
      isNotEnoughWalletBalance: hasInsufficientBalance,
    });
  }

  async loadBlacklistStatus() {
    const response = await checkBlackList();
    if (response.success) {
      this.setState({ isBlacklisted: response.data.is_blacklisted });
    }
  }

  // ─── Event Handlers ────────────────────────────────────────

  handleInputChange(event) {
    const { name, value } = event.target;
    this.setState((prev) => ({
      formInput: { ...prev.formInput, [name]: value },
      fieldErrors: { ...prev.fieldErrors, [name]: undefined },
    }));
  }

  handleDragOver = (e) => {
    e.preventDefault();
    this.setState({ isDraggingOver: true });
  };

  handleDragLeave = () => {
    this.setState({ isDraggingOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    this.setState({ isDraggingOver: false });
    const file = e.dataTransfer.files[0];
    this.processReceiptFile(file);
  };

  handleFileChange = (event) => {
    const file = event.target.files[0];
    this.processReceiptFile(file);
  };

  processReceiptFile(file) {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      this.setState({ receiptFileError: 'Invalid file type. Use JPG, PNG or PDF.' });
      return;
    }

    if (!isFileSizeValid(file)) {
      this.setState({ receiptFileError: 'File too large. Maximum allowed size is 5 MB.' });
      return;
    }

    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }

    const previewUrl = URL.createObjectURL(file);
    this.setState({
      receiptFile: file,
      receiptPreviewUrl: previewUrl,
      receiptFileError: null,
      fieldErrors: { ...this.state.fieldErrors, receiptFile: undefined },
    });
  }

  removeReceiptFile = () => {
    if (this.state.receiptPreviewUrl) {
      URL.revokeObjectURL(this.state.receiptPreviewUrl);
    }
    this.setState({
      receiptFile: null,
      receiptPreviewUrl: null,
      receiptFileError: null,
    });
  };

  openReceiptPreview = () => {
    if (this.state.receiptPreviewUrl) {
      window.open(this.state.receiptPreviewUrl, '_blank');
    }
  };

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  handlePaymentTypeChange(newPaymentType) {
    const isOnline = newPaymentType === 'online';

    if (newPaymentType === 'wallet') {
      this.loadUserWalletInfo();
    }

    this.setState({
      paymentType: newPaymentType,
      requiresOnlinePayment: isOnline,
      showPaymentDetails: isOnline,
    });
  }

  // ─── Online Terms & Conditions (fee disclosure) ─────────────

  openOnlineTermsModal = () => {
    this.setState({ showOnlineTermsModal: true });
  };

  closeOnlineTermsModal = () => {
    this.setState({ showOnlineTermsModal: false });
  };

  // Tapping "I Agree" both unlocks and checks the acceptance checkbox.
  // The user can still uncheck it afterwards to revoke consent, which
  // re-blocks submission (see isSubmitDisabled).
  handleAgreeOnlineTerms = () => {
    this.setState({
      showOnlineTermsModal: false,
      onlineTermsUnlocked: true,
      onlineTermsAccepted: true,
    });
  };

  toggleOnlineTermsAccepted = () => {
    if (!this.state.onlineTermsUnlocked) return;
    this.setState((prev) => ({
      onlineTermsAccepted: !prev.onlineTermsAccepted,
    }));
  };

  getOnlineFeeBreakdown() {
    const amount = parseFloat(this.state.auctionDetails?.payment_amount) || 0;
    const fee = ONLINE_PROCESSING_FEE;
    const subtotal = amount + fee;
    return { amount, fee, subtotal };
  }

  // ─── Enroll Submit ─────────────────────────────────────────

  async handleEnrollSubmit(event) {
    event.preventDefault();

    if (!this.validateOfflineForm()) return;

    const { auctionDetails, paymentType, auctionId } = this.state;

    if (paymentType === 'online') {
      return this.handleOnlineEnrollment();
    }
    const baseAmount = auctionDetails.payment_amount;

    const finalAmount = baseAmount;

    this.setState({
      selectedPriceUnits: finalAmount * PRICE_AMOUNT_MULTIPLIER,
      isSubmitting: true,
    });

    const formData = this.buildEnrollFormData(baseAmount);

    this.setState({ isPaymentRedirecting: true });
    try {
      const response = await userEnrollment(formData);
      if (response.success) {
        this.setState({ showSuccessMessage: true, isSubmitting: false });

        if (paymentType === 'offline') {
          toast.success(
            i18n.t('enrollment_submitted_pending_approval') ||
              'Enrollment submitted! Please wait for admin approval.'
          );
          this.props.navigate(`/auction-details/${auctionId}`);
        } else if (paymentType === 'wallet') {
          toast.success(
            i18n.t('enrollment_completed_successfully') || 'Enrollment completed successfully!'
          );
          this.props.navigate(`/auction-details/${auctionId}`);
        }
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      const res = error?.response?.data || error;

      if (res?.message === 'validation_error' && res?.data) {
        const mappedFieldErrors = this.mapBackendErrors(res.data);

        this.setState((prev) => ({
          fieldErrors: {
            ...prev.fieldErrors,
            ...mappedFieldErrors,
          },
        }));

        toast.error('Please fix the highlighted fields');
      } else {
        toast.error(res?.message || i18n.t('something_went_wrong'));
      }
    } finally {
      this.setState({ isSubmitting: false, isPaymentRedirecting: false });
    }
  }

  mapBackendErrors = (data) => {
    const fieldErrors = {};

    Object.entries(data).forEach(([field, messages]) => {
      fieldErrors[field] = Array.isArray(messages) ? messages[0] : messages;
    });

    return fieldErrors;
  };

  validateOfflineForm() {
    if (this.state.paymentType !== 'offline') return true;

    const { formInput, receiptFile } = this.state;

    const fieldErrors = {};

    const isEmpty = (value) => value == null || value === 'null' || String(value).trim() === '';

    const receiptNumber = formInput.receipt_number?.trim();

    if (isEmpty(formInput.bank)) {
      fieldErrors.bank = i18n.t('field_required');
    }

    if (isEmpty(formInput.account_number)) {
      fieldErrors.account_number = i18n.t('field_required');
    }

    if (isEmpty(receiptNumber)) {
      fieldErrors.receipt_number = i18n.t('field_required');
    } else if (!/^[A-Za-z0-9!@#$%^&*()_\-+=[\]{}:;"'<>?,./]{4,}$/.test(receiptNumber)) {
      fieldErrors.receipt_number = i18n.t('receipt_min_4_chars');
    }

    if (isEmpty(formInput.transfer_date)) {
      fieldErrors.transfer_date = i18n.t('field_required');
    }

    if (isEmpty(formInput.beneficiary)) {
      fieldErrors.beneficiary = i18n.t('field_required');
    }

    if (!receiptFile) {
      fieldErrors.receiptFile = i18n.t('field_required');
    }

    this.setState({ fieldErrors });

    return Object.keys(fieldErrors).length === 0;
  }

  buildEnrollFormData(paymentAmount) {
    const { auctionId, formInput, isCompany, requiresOnlinePayment, paymentType, receiptFile } =
      this.state;

    const fd = new FormData();
    fd.append('auction', auctionId);
    fd.append('enroll_name', formInput.name);
    fd.append('bank', formInput.bank);
    fd.append('account_number', formInput.account_number);
    fd.append('beneficiary', formInput.beneficiary);
    fd.append('receipt_number', formInput.receipt_number);
    fd.append('transfer_date', formInput.transfer_date || '');
    fd.append('is_company', Boolean(isCompany));
    fd.append('is_offline', !requiresOnlinePayment);
    fd.append('ptype', paymentType);
    fd.append('identity_type', 'Civil Card');
    fd.append('amount', paymentAmount);

    if (receiptFile) {
      fd.append('file_receipt', receiptFile, receiptFile.name);
    }

    return fd;
  }

  handleOnlineEnrollment = async () => {
    const { auctionDetails } = this.state;

    try {
      const { amount, fee } = this.getOnlineFeeBreakdown();
      // NOTE: confirm the exact field names/contract with the backend —
      // `amount` here is the TOTAL charged via the payment gateway
      // (enroll amount + non-refundable documentation/admin fee).
      const payload = {
        auction_id: auctionDetails.id,
        amount,
        fee,
      };
      const data = await initiateOnlineEnroll(payload);
      console.log(data, 'data');

      this.setState({
        redirectData: {
          actionUrl: data.action_url,
          accessCode: data.access_code,
          encRequest: data.enc_request,
        },
      });
    } catch (err) {
      const res = err.response?.data || err;
      toast.error(res?.message || i18n.t('something_went_wrong'));
      this.setState({ isPaymentRedirecting: false });
    }
  };

  // ─── Render Helpers ────────────────────────────────────────

  get isSubmitDisabled() {
    const {
      termsAccepted,
      rulesAccepted,
      isEnrolled,
      isProfileComplete,
      paymentType,
      isNotEnoughWalletBalance,
      isBlacklisted,
      auctionDetails,
      onlineTermsAccepted,
    } = this.state;

    const isVisitRequired =
      Number(auctionDetails.visit_amount) > 0 && auctionDetails.is_visit_active;

    // Online payment additionally requires the fee-disclosure Terms &
    // Conditions to be accepted, and a valid enroll amount to charge.
    const isOnlineBlocked =
      paymentType === 'online' &&
      (!onlineTermsAccepted || !(Number(auctionDetails.payment_amount) > 0));

    return (
      !termsAccepted ||
      !rulesAccepted ||
      isEnrolled ||
      !isProfileComplete ||
      (paymentType === 'wallet' && isNotEnoughWalletBalance) ||
      auctionDetails.is_enrolled ||
      isVisitRequired ||
      isBlacklisted ||
      isOnlineBlocked
    );
  }

  get canShowSubmitSection() {
    const { paymentType, isNotEnoughWalletBalance } = this.state;
    return !(paymentType === 'wallet' && isNotEnoughWalletBalance);
  }

  get localizedAuctionTitle() {
    const lang = localStorage.getItem('I18N_LANGUAGE');
    return lang === 'ar' ? this.state.auctionDetails.title_ar : this.state.auctionDetails.title;
  }

  get localizedGroupName() {
    const lang = localStorage.getItem('I18N_LANGUAGE');
    const groupInfo = this.state.auctionDetails?.group_info;
    if (!groupInfo) return null;
    return lang === 'ar'
      ? groupInfo.group_name_ar || groupInfo.group_name
      : groupInfo.group_name || groupInfo.group_name_ar;
  }
  handleAcknowledgementView = async () => {
    const { auctionDetails } = this.state;
    const lang = localStorage.getItem('I18N_LANGUAGE') ?? 'en';

    this.setState({ isAcknowledgementLoading: true });

    try {
      const blob = await fetchAcknowledgementPdf(auctionDetails.id, lang);
      openBlobInNewTab(blob);
    } catch (error) {
      console.error('[AcknowledgementView]', error);
      toast.error(this.props.t('errors.acknowledgement_pdf_unavailable'));
    } finally {
      this.setState({ isAcknowledgementLoading: false });
    }
  };

  // ─── Bank Details: copy / share / download ──────────────────

  getBankAccount = () => {
    const paymentAccounts = this.state.auctionDetails?.payment_accounts;
    const enrollmentAccount = paymentAccounts?.auction_enrollment?.bank_account;
    if (enrollmentAccount) return enrollmentAccount;

    // Fallback: organization has no dedicated enrollment account configured —
    // use Mzadcom's default account instead (fields are a subset: id, account_name,
    // bank_name, iban, is_default — no account_number/swift_code/currency/branch_name).
    const defaultMzadcom = paymentAccounts?.default_mzadcom;
    if (defaultMzadcom) return defaultMzadcom;

    return null;
  };

  isUsingFallbackBankAccount = () => {
    const paymentAccounts = this.state.auctionDetails?.payment_accounts;
    return !paymentAccounts?.auction_enrollment?.bank_account && !!paymentAccounts?.default_mzadcom;
  };

  handleCopyField = async (key, value) => {
    try {
      await navigator.clipboard.writeText(String(value));
      this.setState({ copiedField: key });
      window.clearTimeout(this._copyTimeout);
      this._copyTimeout = window.setTimeout(() => {
        this.setState((s) => (s.copiedField === key ? { copiedField: null } : null));
      }, 1500);
    } catch (err) {
      toast.error(i18n.t('copy_failed') || 'Could not copy to clipboard');
    }
  };

  buildBankDetailsText = () => {
    const { auctionDetails } = this.state;
    const bankAccount = this.getBankAccount();
    if (!bankAccount) return '';

    const lines = [
      `*${i18n.t('auction_enrollment_payment') || 'Auction Enrollment Payment'}*`,
      `${this.localizedAuctionTitle || '--'} (MZAD-${auctionDetails.id})`,
      auctionDetails.payment_amount
        ? `${i18n.t('enroll_amount') || 'Enroll Amount'}: ${formatCurrency(auctionDetails.payment_amount)} OMR`
        : null,
      '',
      `*${i18n.t('bank_details') || 'Bank Transfer Details'}*`,
      ...BANK_DETAIL_FIELDS.filter((f) => bankAccount[f.key]).map(
        (f) => `${f.label}: ${bankAccount[f.key]}`
      ),
      '',
      `⚠️ ${i18n.t('verify_bank_details_note') || 'Please verify these details carefully before transferring the amount.'}`,
    ].filter((line) => line !== null);

    return lines.join('\n');
  };

  handleWhatsAppShare = () => {
    const text = this.buildBankDetailsText();
    if (!text) {
      toast.error(i18n.t('bank_details_unavailable') || 'Bank details are not available yet');
      return;
    }
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  wrapCenteredText = (ctx, text, centerX, startY, maxWidth, lineHeight) => {
    const words = text.split(' ');
    let line = '';
    let y = startY;
    words.forEach((word, i) => {
      const testLine = line + word + ' ';
      if (ctx.measureText(testLine).width > maxWidth && i > 0) {
        ctx.fillText(line.trim(), centerX, y);
        line = word + ' ';
        y += lineHeight;
      } else {
        line = testLine;
      }
    });
    ctx.fillText(line.trim(), centerX, y);
  };
  getCanvasFontFamily = (isArabic) =>
    isArabic ? '"Cairo", "Tahoma", "Arial", sans-serif' : '"DM Sans", sans-serif';

  // Canvas doesn't auto-wait for webfonts the way the DOM does — without this,
  // Arabic glyphs can silently fall back to tofu boxes on first render.
  loadCanvasFonts = async (fontFamily) => {
    const weights = [
      '400 11.5px',
      '700 12px',
      '700 12.5px',
      '700 13px',
      'italic 700 14px',
      '700 19px',
      '700 22px',
    ];
    try {
      await Promise.all(weights.map((w) => document.fonts.load(`${w} ${fontFamily}`)));
      await document.fonts.ready;
    } catch (e) {
      // Font API not available/failed — canvas will fall back to default sans-serif
    }
  };
  handleDownloadBankCard = async () => {
    const bankAccount = this.getBankAccount();
    const { auctionDetails } = this.state;
    if (!bankAccount) {
      toast.error(i18n.t('bank_details_unavailable') || 'Bank details are not available yet');
      return;
    }

    this.setState({ isGeneratingImage: true });

    try {
      const isArabic = localStorage.getItem('I18N_LANGUAGE') === 'ar';
      const dir = isArabic ? 'rtl' : 'ltr';
      const fontFamily = this.getCanvasFontFamily(isArabic);
      await this.loadCanvasFonts(fontFamily);

      const font = (weight, size) => `${weight} ${size}px ${fontFamily}`;

      // Mirrors the table order/labels used on the printed enrollment form
      const tableFields = [
        {
          label: i18n.t('beneficiary_name') || (isArabic ? 'اسم المستفيد' : 'Beneficiary Name'),
          value: bankAccount.account_name,
        },
        {
          label: i18n.t('bank_name') || (isArabic ? 'اسم البنك' : 'Bank Name'),
          value: bankAccount.bank_name,
        },
        {
          label: i18n.t('account_number') || (isArabic ? 'رقم الحساب' : 'Account Number'),
          value: bankAccount.account_number,
        },
        { label: 'IBAN', value: bankAccount.iban },
        {
          label: i18n.t('swift_code') || (isArabic ? 'رمز السويفت' : 'SWIFT Code'),
          value: bankAccount.swift_code,
        },
      ].filter((f) => f.value);

      const scale = 2;
      const width = 720;
      const margin = 40;
      const innerWidth = width - margin * 2;

      const headerHeight = 118;
      const instructionHeight = 40;
      const amountBlockHeight = 60;
      const rowHeight = 46;
      const tableHeight = tableFields.length * rowHeight;
      const footerHeight = 108; // 4 lines: group name, MZAD ID, reg start, reg end
      const bottomPad = 28;

      const height =
        margin +
        headerHeight +
        instructionHeight +
        amountBlockHeight +
        tableHeight +
        footerHeight +
        bottomPad +
        margin;

      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      if ('direction' in ctx) ctx.direction = dir;

      // ── page background + outer border ──
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1;
      ctx.strokeRect(0.5, 0.5, width - 1, height - 1);

      const centerX = width / 2;
      const startEdge = isArabic ? width - margin : margin; // "leading" edge for left/right-aligned lines
      const edgeAlign = isArabic ? 'right' : 'left';
      let y = margin;

      // ── Company name (bold, centered) ──
      ctx.fillStyle = '#111827';
      ctx.textAlign = 'center';
      ctx.font = font('700', 19);
      ctx.fillText(
        i18n.t('mzadcom_company_name') ||
          (isArabic ? 'مزادكم لحلول المزادات الذكية' : 'Mazadcom Smart Auction Solutions LLC'),
        centerX,
        y + 8
      );

      // ── Subtitle (italic, centered) ──
      ctx.font = font('italic 700', 14);
      ctx.fillStyle = '#0c8da2';
      ctx.fillText(
        i18n.t('auction_enrollment_payment_advice') ||
          (isArabic ? 'إشعار تسجيل ودفع المزاد' : 'Auction Enrollment & Payment Advice'),
        centerX,
        y + 34
      );

      // ── divider ──
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(margin, y + 54);
      ctx.lineTo(width - margin, y + 54);
      ctx.stroke();

      // ── instruction line (small, italic, grey, centered, wraps) ──
      ctx.font = font('italic 400', 11.5);
      ctx.fillStyle = '#6b7280';
      const instruction =
        i18n.t('bank_reference_instruction') ||
        (isArabic
          ? 'يمكن طباعة هذا المستند وتقديمه إلى البنك كمرجع للدفع لمبلغ الضمان / التسجيل.'
          : 'This document may be printed and presented to a bank as payment reference for the guarantee / enrollment amount.');
      this.wrapCenteredText(ctx, instruction, centerX, y + 78, innerWidth, 15);

      y += headerHeight + instructionHeight - 30;

      // ── Enrollment / Guarantee Amount label ──
      ctx.textAlign = edgeAlign;
      ctx.font = font('700', 12.5);
      ctx.fillStyle = '#374151';
      ctx.fillText(
        (
          i18n.t('guarantee_amount') ||
          (isArabic ? 'مبلغ التسجيل / الضمان' : 'Enrollment / Guarantee Amount')
        ).toUpperCase(),
        startEdge,
        y + 4
      );

      // ── amount (aligned to leading edge, plain text, black, no border/bg) ──
      ctx.font = font('700', 22);
      ctx.fillStyle = '#111827';
      const currencyLabel = i18n.t('omr') || 'OMR';
      const amountText = auctionDetails.payment_amount
        ? isArabic
          ? `${formatCurrency(auctionDetails.payment_amount)} ${currencyLabel}`
          : `${currencyLabel} ${formatCurrency(auctionDetails.payment_amount)}`
        : `${currencyLabel} __________________`;
      ctx.fillText(amountText, startEdge, y + 40);

      y += 50;

      // ── bordered table: label | value (column order mirrors for RTL) ──
      const labelColWidth = 190;
      const tableTop = y;
      ctx.strokeStyle = '#d1d5db';
      ctx.lineWidth = 1;
      ctx.strokeRect(margin + 0.5, tableTop + 0.5, innerWidth - 1, tableHeight - 1);

      // In RTL, the label column sits on the right (reading starts from the right)
      const labelColX = isArabic ? margin + innerWidth - labelColWidth : margin;
      const colDividerX = isArabic ? margin + innerWidth - labelColWidth : margin + labelColWidth;
      const labelTextX = isArabic ? labelColX + labelColWidth - 14 : labelColX + 14;
      const valueColStart = isArabic ? margin : margin + labelColWidth;
      const valueTextX = isArabic
        ? valueColStart + innerWidth - labelColWidth - 14
        : valueColStart + 14;

      tableFields.forEach((field, idx) => {
        const rowY = tableTop + idx * rowHeight;

        // label cell background
        ctx.fillStyle = '#e5e5e5';
        ctx.fillRect(labelColX, rowY, labelColWidth, rowHeight);

        // row separators
        if (idx > 0) {
          ctx.strokeStyle = '#d1d5db';
          ctx.beginPath();
          ctx.moveTo(margin, rowY);
          ctx.lineTo(margin + innerWidth, rowY);
          ctx.stroke();
        }
        // column separator
        ctx.strokeStyle = '#d1d5db';
        ctx.beginPath();
        ctx.moveTo(colDividerX, rowY);
        ctx.lineTo(colDividerX, rowY + rowHeight);
        ctx.stroke();

        // label text
        ctx.textAlign = edgeAlign;
        ctx.font = font('700', 12);
        ctx.fillStyle = '#374151';
        ctx.fillText(field.label, labelTextX, rowY + rowHeight / 2 + 4);

        // value text (kept LTR-rendered even in Arabic mode — IBAN/account numbers are Latin/digits)
        ctx.font = font('700', 13);
        ctx.fillStyle = '#111827';
        ctx.fillText(String(field.value), valueTextX, rowY + rowHeight / 2 + 4);
      });

      y = tableTop + tableHeight + 26;

      // ── footer: Auction Group Name / MZAD ID / Reg dates ──
      ctx.textAlign = edgeAlign;
      ctx.font = font('700', 12);
      ctx.fillStyle = '#374151';

      const footerLines = [
        `${i18n.t('group_name') || (isArabic ? 'اسم مجموعة المزاد' : 'Auction Group Name')} - ${
          this.localizedGroupName || '—'
        }`,
        `MZAD ID - MZAD-${auctionDetails.id}`,
        `${i18n.t('reg_start_date') || (isArabic ? 'تاريخ بدء التسجيل' : 'Auction Reg Start Date')} - ${
          auctionDetails.reg_start_date_formatted || '—'
        }`,
        `${i18n.t('reg_end_date') || (isArabic ? 'تاريخ انتهاء التسجيل' : 'Auction Reg End Date')} - ${
          auctionDetails.reg_end_date_formatted || '—'
        }`,
      ];

      footerLines.forEach((line, idx) => {
        ctx.fillText(line, startEdge, y + idx * 22);
      });

      canvas.toBlob((blob) => {
        this.setState({ isGeneratingImage: false });
        if (!blob) {
          toast.error(i18n.t('something_went_wrong'));
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `bank-details-MZAD-${auctionDetails.id}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (err) {
      console.error('[BankDetailsDownload]', err);
      toast.error(i18n.t('something_went_wrong'));
      this.setState({ isGeneratingImage: false });
    }
  };
  // ─── Sub-renderers ─────────────────────────────────────────

  renderAuctionInfoCard() {
    const { auctionDetails } = this.state;

    return (
      <div className="mze-panel">
        <div className="mze-panel__header">
          <Info size={16} />
          <span>{i18n.t('auction_details') || 'Auction Details'}</span>
        </div>
        <div className="mze-panel__body">
          <div className="mze-auction-info">
            <div className="mze-auction-info__left">
              <span className="mze-auction-info__id">MZAD-{auctionDetails.id}</span>
              <h3 className="mze-auction-info__title">{this.localizedAuctionTitle}</h3>
              {this.localizedGroupName && (
                <span className="mze-auction-info__group">
                  <Layers size={12} />
                  {this.localizedGroupName}
                </span>
              )}
            </div>
            {auctionDetails.payment_amount && (
              <div className="mze-auction-info__right">
                <span className="mze-auction-info__label">{i18n.t('enroll_amount')}</span>
                <span className="mze-auction-info__amount">
                  {formatCurrency(auctionDetails.payment_amount)}{' '}
                  <OmrRiyal width={18} height={18} />
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderAlerts() {
    const { isBlacklisted, isProfileComplete, missingProfileFields, auctionDetails } = this.state;
    const isVisitRequired =
      Number(auctionDetails.visit_amount) > 0 && auctionDetails.is_visit_active;

    return (
      <>
        {isBlacklisted && (
          <div className="mze-alert mze-alert--danger">
            <Ban size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <strong>{i18n.t('blocked')}</strong>
              <p>{i18n.t('user_blacklisted_message')}</p>
            </div>
          </div>
        )}

        {!isProfileComplete && (
          <div className="mze-alert mze-alert--warning">
            <ShieldAlert size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <strong>{i18n.t('profile_incomplete') || 'Profile Incomplete'}</strong>
              <p>{i18n.t('profile_completion_message_auction')}</p>
              <div className="mze-alert__badges">
                {missingProfileFields.map((fieldKey, index) => (
                  <span key={index} className="mze-missing-badge">
                    {PROFILE_FIELD_LABELS[fieldKey]}
                  </span>
                ))}
              </div>
              <Link to="/profile" className="mze-alert__link">
                {i18n.t('complete_profile') || 'Complete Profile'} →
              </Link>
            </div>
          </div>
        )}

        {isVisitRequired && (
          <div className="mze-alert mze-alert--danger">
            <AlertCircle size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <p>
                {auctionDetails.is_visit_active
                  ? i18n.t('visit_warning_message')
                  : i18n.t('visit_warning_message_np')}
              </p>
              {auctionDetails.visit_amount && auctionDetails.is_visit_active && (
                <VisitNow
                  amount={auctionDetails.visit_amount}
                  type="sitevisit"
                  id={auctionDetails.id}
                  gid={auctionDetails.group}
                  is_group={auctionDetails.is_grouped}
                />
              )}
            </div>
          </div>
        )}

        {auctionDetails.is_enrolled && (
          <div className="mze-alert mze-alert--success">
            <CheckCircle size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              <strong>{i18n.t('enrolled') || 'Enrolled'}</strong>
              <p>{i18n.t('enrolled_message')}</p>
              <Link to="/" className="mze-alert__link mze-alert__link--success">
                {i18n.t('home')} →
              </Link>
            </div>
          </div>
        )}
      </>
    );
  }

  renderPaymentTypeTabs() {
    const { auctionDetails, paymentType } = this.state;
    const groupInfo = auctionDetails?.group_info;

    return (
      <div className="mze-tabs">
        {groupInfo?.can_offline && (
          <button
            className={`mze-tab ${paymentType === 'offline' ? 'mze-tab--active' : ''}`}
            onClick={() => this.handlePaymentTypeChange('offline')}
          >
            <Building2 size={16} />
            <span>{i18n.t('bank_transfer')}</span>
          </button>
        )}
        {/* {groupInfo?.can_online && (
          <button
            className={`mze-tab ${paymentType === 'online' ? 'mze-tab--active' : ''}`}
            onClick={() => this.handlePaymentTypeChange('online')}
          >
            <CreditCard size={16} />
            <span>{i18n.t('online')}</span>
          </button>
        )} */}
        {groupInfo?.can_wallet && (
          <button
            className={`mze-tab ${paymentType === 'wallet' ? 'mze-tab--active' : ''}`}
            onClick={() => this.handlePaymentTypeChange('wallet')}
          >
            <Wallet size={16} />
            <span>{i18n.t('wallet')}</span>
          </button>
        )}
      </div>
    );
  }

  handleReceiptScan = async () => {
    const { receiptFile } = this.state;
    if (!receiptFile) return;

    this.setState({ isScanning: true, scanError: null });

    try {
      let uploadFile = receiptFile;

      // Convert PDF → PNG
      if (receiptFile.type === 'application/pdf') {
        uploadFile = await convertPdfToImage(receiptFile);
      }
      const formData = new FormData();
      formData.append('receipt', uploadFile);

      const result = await scanReceipt(formData);

      if (result.success && result.data) {
        const {
          bank_name,
          account_number,
          receipt_number,
          amount,
          currency,
          transfer_date,
          beneficiary_name,
        } = result.data.form;

        this.setState((prev) => ({
          formInput: {
            ...prev.formInput,
            bank: bank_name || prev.formInput.bank,
            account_number: account_number || prev.formInput.account_number,
            receipt_number: receipt_number || prev.formInput.receipt_number,
            beneficiary: beneficiary_name || prev.formInput.beneficiary,

            ...(amount && {
              amount: currency ? `${amount} ${currency}` : amount,
            }),

            transfer_date: transfer_date || prev.formInput.transfer_date,
          },
          isScanning: false,
          scanSuccess: true,
        }));

        toast.success('Receipt scanned successfully! Please verify the fields.');
      } else {
        this.setState({
          isScanning: false,
          scanError: 'Could not extract data. Please fill manually.',
        });
      }
    } catch (err) {
      console.error('[ReceiptScan]', err);
      this.setState({
        isScanning: false,
        scanError: err.message || 'Scan failed. Please fill the form manually.',
      });
    }
  };

  renderFieldError(field) {
    const msg = this.state.fieldErrors[field];
    if (!msg) return null;
    return (
      <span className="mze-field__error">
        <AlertCircle size={12} />
        {msg}
      </span>
    );
  }

  renderOfflinePaymentForm() {
    const {
      isProfileComplete,
      formInput,
      receiptFile,
      receiptPreviewUrl,
      receiptFileError,
      isDraggingOver,
    } = this.state;

    const today = new Date().toISOString().split('T')[0];
    const isPdf = receiptFile?.type === 'application/pdf';
    const isImage = receiptFile?.type?.startsWith('image/');

    return (
      <div className="mze-form-block">
        <div className="mze-policy">
          <strong className="mze-policy__title">{i18n.t('guarantee_amount_policy_head')}</strong>
          <div className="mze-policy__text">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <p key={n}>{i18n.t(`guarantee_amount_policy_${n}`)}</p>
            ))}
          </div>
        </div>

        {/* ── Receipt Upload ── */}
        <div className="mze-field mze-field--upload">
          <label className="mze-field__label">
            <Upload size={14} />
            {i18n.t('receipt')}
            <span className="mze-field__required">*</span>
          </label>

          <input
            type="file"
            id="file_receipt"
            name="file_receipt"
            accept=".jpg,.jpeg,.png,.pdf"
            style={{ display: 'none' }}
            disabled={!isProfileComplete}
            onChange={this.handleFileChange}
          />

          {!receiptFile ? (
            <div
              className={`mze-upload-zone ${isDraggingOver ? 'mze-upload-zone--dragover' : ''} ${!isProfileComplete ? 'mze-upload-zone--disabled' : ''}`}
              onDragOver={this.handleDragOver}
              onDragLeave={this.handleDragLeave}
              onDrop={this.handleDrop}
            >
              <div className="mze-upload-zone__icon">
                <Upload size={24} />
              </div>
              <p className="mze-upload-zone__title">{i18n.t('drop_receipt_here')}</p>
              <p className="mze-upload-zone__sub">JPG, PNG or PDF — max 5 MB</p>
              <button
                type="button"
                className="mze-upload-zone__btn"
                disabled={!isProfileComplete}
                onClick={() => document.getElementById('file_receipt').click()}
              >
                {i18n.t('browse_file')}
              </button>
            </div>
          ) : (
            <div className="mze-receipt-preview">
              <div className="mze-receipt-preview__header">
                <FileText size={15} className="mze-receipt-preview__file-icon" />
                <span className="mze-receipt-preview__name">{receiptFile.name}</span>
                <span className="mze-receipt-preview__size">
                  {this.formatFileSize(receiptFile.size)}
                </span>
                <button
                  type="button"
                  className="mze-receipt-preview__remove"
                  onClick={this.removeReceiptFile}
                >
                  <X size={12} /> {i18n.t('remove')}
                </button>
              </div>

              <div
                className={`mze-receipt-preview__body ${isImage ? 'mze-receipt-preview__body--image' : ''}`}
              >
                {isImage && (
                  <img
                    src={receiptPreviewUrl}
                    alt="Receipt preview"
                    className="mze-receipt-preview__img"
                  />
                )}
                {isPdf && (
                  <div className="mze-receipt-preview__pdf">
                    <FileText size={32} className="mze-receipt-preview__pdf-icon" />
                    <div>
                      <p className="mze-receipt-preview__pdf-name">{receiptFile.name}</p>
                      <span className="mze-receipt-preview__pdf-meta">
                        PDF document · {this.formatFileSize(receiptFile.size)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="mze-receipt-preview__actions">
                <button
                  type="button"
                  className="mze-receipt-preview__view-btn"
                  onClick={this.openReceiptPreview}
                >
                  <FileText size={13} /> {i18n.t('view_full_size')}
                </button>
                <button
                  type="button"
                  className="mze-receipt-preview__change-btn"
                  onClick={() => document.getElementById('file_receipt').click()}
                >
                  <Upload size={13} /> {i18n.t('change_file')}
                </button>
              </div>
            </div>
          )}

          {receiptFileError && (
            <div className="mze-upload-error">
              <AlertCircle size={13} />
              {receiptFileError}
            </div>
          )}

          {this.renderFieldError('receiptFile')}

          {receiptFile && !receiptFileError && (
            <>
              {/* ── AI Scan Button ── */}
              <div className="mze-scan-row">
                <button
                  type="button"
                  className={`ai-scan-btn ${this.state.isScanning ? 'ai-scan-btn--scanning' : ''} ${this.state.scanSuccess ? 'ai-scan-btn--success' : ''}`}
                  onClick={this.handleReceiptScan}
                  disabled={this.state.isScanning || this.state.scanSuccess}
                >
                  {this.state.isScanning ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      <span>{i18n.t('scanning_receipt')}</span>
                      <span className="ai-scan-btn__dots">
                        <span />
                        <span />
                        <span />
                      </span>
                    </>
                  ) : this.state.scanSuccess ? (
                    <>
                      <Check size={16} />
                      <span>{i18n.t('fields_filled')}</span>
                    </>
                  ) : (
                    <>
                      <span className="ai-scan-btn__glow" />
                      <ScanLine size={16} />
                      <span>{i18n.t('auto_fill_from_receipt')}</span>
                      <span className="ai-scan-btn__badge">{i18n.t('ai')}</span>
                    </>
                  )}
                </button>
              </div>

              {this.state.scanError && (
                <div className="mze-upload-error" style={{ marginTop: 6 }}>
                  <AlertCircle size={13} />
                  {this.state.scanError}
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Bank Fields ── */}
        <div className="mze-offline-fields">
          {OFFLINE_BANK_FIELDS.map((field) => (
            <div
              className={`mze-field ${field.name === 'beneficiary' ? 'mze-field--full' : ''}`}
              key={field.name}
            >
              <label className="mze-field__label">
                <span className="mze-field__required">*</span>
                {field.label}
              </label>
              <input
                className={`mze-field__input ${this.state.fieldErrors?.[field.name] ? ' input-error' : ''}`}
                type={field.type}
                name={field.name}
                placeholder={field.placeholder}
                max={field.type === 'date' ? today : undefined}
                value={formInput[field.name] || ''}
                onChange={this.handleInputChange}
              />
              {this.renderFieldError(field.name)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Online payment: enroll amount / fee / subtotal breakdown ──
  renderFeeSummary() {
    const { amount, fee, subtotal } = this.getOnlineFeeBreakdown();
    const hasAmount = amount > 0;

    return (
      <div className="mze-fee-summary">
        <div className="mze-fee-summary__header">
          <Wallet size={14} />
          <span>{i18n.t('payment_summary')}</span>
        </div>

        <div className="mze-fee-row">
          <span className="mze-fee-row__label">{i18n.t('enroll_amount')}</span>
          <span className="mze-fee-row__value">
            {hasAmount ? amount.toFixed(3) : '0.000'} <OMR size={9} strokeWidth={2} />
          </span>
        </div>

        <div className="mze-fee-row mze-fee-row--fee">
          <span className="mze-fee-row__label">
            {i18n.t('documentation_admin_fee')}
            <span className="mze-nonrefundable-badge">
              <Ban size={10} />
              {i18n.t('non_refundable')}
            </span>
          </span>
          <span className="mze-fee-row__value mze-fee-row__value--fee">
            {fee.toFixed(3)} <OMR size={9} strokeWidth={2} color={'#d97706'} />
          </span>
        </div>

        <div className="mze-fee-row mze-fee-row--total">
          <span className="mze-fee-row__label">{i18n.t('subtotal')}</span>
          <span className="mze-fee-row__value">
            {subtotal.toFixed(3)} <OMR size={9} strokeWidth={2} color={'#087384'} />
          </span>
        </div>
      </div>
    );
  }

  // ── Online payment: Terms & Conditions link + checkbox ──
  renderOnlineTermsAcceptance() {
    const { onlineTermsUnlocked, onlineTermsAccepted, fieldErrors } = this.state;

    return (
      <div className="mze-tnc-block">
        <div className="mze-tnc-link-row">
          <button type="button" className="mze-tnc-link" onClick={this.openOnlineTermsModal}>
            <FileText size={14} />
            {i18n.t('terms_and_conditions') || 'Terms & Conditions'}
          </button>
          <span className="mze-tnc-link-hint">
            {i18n.t('open_to_read_and_agree') ||
              'Open to read the full text, then tap "I Agree" at the bottom.'}
          </span>
        </div>

        <label className={`mze-check ${!onlineTermsUnlocked ? 'mze-check--disabled' : ''}`}>
          <input
            type="checkbox"
            checked={onlineTermsAccepted}
            disabled={!onlineTermsUnlocked}
            onChange={this.toggleOnlineTermsAccepted}
          />
          <span className="mze-check__box">
            <Check />
          </span>
          <span className="mze-check__text">
            {i18n.t('i_have_read_and_accept_terms') ||
              'I have read and accept the Terms & Conditions.'}
          </span>
        </label>

        {!onlineTermsUnlocked && (
          <p className="mze-tnc-hint">
            <Info size={12} />
            {i18n.t('checkbox_disabled_hint') ||
              'The checkbox stays disabled until you complete the Terms & Conditions step (I Agree).'}
          </p>
        )}

        {fieldErrors?.onlineTerms && (
          <span className="mze-field__error">
            <AlertCircle size={12} />
            {fieldErrors.onlineTerms}
          </span>
        )}
      </div>
    );
  }

  // ── Online payment: Terms & Conditions modal ──
  renderOnlineTermsModal() {
    if (!this.state.showOnlineTermsModal) return null;
    const { showOnlineTermsModal } = this.state;
    const terms = [i18n.t('ack_term_1'), i18n.t('ack_term_2'), i18n.t('ack_term_3')];

    const footer = (
      <div className="mze-ack-footer">
        <button type="button" className="modal-btn btn-cancel" onClick={this.closeOnlineTermsModal}>
          <X size={14} />
          <span>{i18n.t('close')}</span>
        </button>
        <button
          type="button"
          className="modal-btn btn-confirm"
          onClick={this.handleAgreeOnlineTerms}
        >
          <Check size={14} />
          <span>{i18n.t('i_agree')}</span>
        </button>
      </div>
    );

    return (
      <CustomModal
        isOpen={showOnlineTermsModal}
        header={i18n.t('auction_agreement_acknowledgement')}
        headerIcon={<FileText size={18} />}
        footer={footer}
        modalClassName="mze-ack-modal"
        closeBtn={false}
      >
        {/* ── Terms ── */}
        <div className="mze-ack-block">
          <div className="mze-ack-block__head">
            <FileText size={13} />
            <span>{i18n.t('acknowledgement_of_terms')}</span>
          </div>
          <div className="mze-ack-block__body mze-ack-block__body--terms">
            {terms.map((term, idx) => (
              <div key={idx} className="mze-ack-term">
                <div className="mze-ack-term__num">{idx + 1}</div>
                <p className="mze-ack-term__text">{term}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Legal notice ── */}
        <div className="mze-ack-notice">
          <AlertCircle size={14} />
          <p>{i18n.t('ack_legal_notice')}</p>
        </div>
      </CustomModal>
    );
  }

  renderOnlinePaymentForm() {
    return (
      <div className="mze-form-block">
        <div className="mze-policy">
          <strong className="mze-policy__title">
            {i18n.t('online_deposit_and_refund_policy')}
          </strong>
          <div className="mze-policy__text">
            {ONLINE_POLICY_POINTS.map((point, index) => (
              <p key={index}>
                {index + 1}) {point}
              </p>
            ))}
          </div>
        </div>

        {this.renderFeeSummary()}
        {this.renderOnlineTermsAcceptance()}
        {this.renderOnlineTermsModal()}
      </div>
    );
  }

  renderWalletPaymentForm() {
    const { walletBalance, isNotEnoughWalletBalance } = this.state;

    return (
      <div className="mze-form-block">
        <div className="mze-wallet-bar">
          <Wallet size={18} className="mze-wallet-bar__icon" />
          <span className="mze-wallet-bar__text">
            {i18n.t('balance')}:
            <strong className="mze-wallet-bar__amount">
              {formatCurrency(walletBalance)} {i18n.t('omr')}
            </strong>
          </span>
        </div>

        {isNotEnoughWalletBalance && (
          <>
            <div className="mze-alert mze-alert--danger mze-alert--compact">
              <AlertCircle size={16} className="mze-alert__icon" />
              <span>{i18n.t('insufficient_wallet_balance')}</span>
            </div>
            <NavLink to="/wallet-manage" className="mze-add-fund">
              <Plus size={18} />
              <span>{i18n.t('add_fund')}</span>
            </NavLink>
          </>
        )}
      </div>
    );
  }

  renderThawaniSelector() {
    const { showPaymentDetails, selectedCardType } = this.state;
    if (!showPaymentDetails) return null;

    return (
      <div className="mze-thawani">
        <img src={logoThawani} alt="Thawani" className="mze-thawani__logo" />
        <p className="mze-thawani__text">{i18n.t('redirect_to_thawani')}</p>
        <div className="mze-card-types">
          <button
            className={`mze-card-type ${selectedCardType === 'local' ? 'mze-card-type--active' : ''}`}
            onClick={() => this.setState({ selectedCardType: 'local' })}
          >
            {i18n.t('local_cards_ecommended')}
          </button>
          <button
            className={`mze-card-type ${selectedCardType === 'credit' ? 'mze-card-type--active' : ''}`}
            onClick={() => this.setState({ selectedCardType: 'credit' })}
          >
            {i18n.t('credit_cards_coast_2')}
          </button>
        </div>
      </div>
    );
  }

  renderSubmitSection() {
    const {
      termsAccepted,
      rulesAccepted,
      isEnrolled,
      isSubmitting,
      isPaymentRedirecting,
      showSuccessMessage,
      auctionId,
      isAcknowledgementLoading,
      auctionDetails,
      paymentType,
    } = this.state;

    // Offline/wallet submission just moves the enrollment forward; online
    // actually charges the gateway for (enroll amount + non-refundable fee),
    // so the button reflects that with a "Pay" label + amount.
    const isOnline = paymentType === 'online';
    const { subtotal } = this.getOnlineFeeBreakdown();
    const hasEnrollAmount = Number(auctionDetails.payment_amount) > 0;

    return (
      <>
        {this.canShowSubmitSection && (
          <div className="mze-submit-area">
            <div className="mze-rules-row">
              <label className="mze-check">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={() => this.setState({ termsAccepted: !termsAccepted })}
                />
                <span className="mze-check__box">
                  {termsAccepted && <Check size={12} strokeWidth={3} />}
                </span>
                <span className="mze-check__text">
                  {i18n.t('agree_to_terms')} <span className="mze-field__required">*</span>
                </span>
              </label>

              <div className="mze-rules-actions">
                <button
                  type="button"
                  className="mze-rules-btn"
                  onClick={() => {
                    if (!auctionDetails.file_terms) {
                      toast.error('No files found. Contact admin.');

                      return;
                    }
                    window.open(auctionDetails.file_terms, '_blank');
                  }}
                >
                  <Eye size={14} />
                  <span>{i18n.t('preview') || 'Preview'}</span>
                </button>
              </div>
            </div>

            <div className="mze-rules-row">
              <label
                className="mze-check"
                onClick={(e) => {
                  e.preventDefault();
                  if (!rulesAccepted) {
                    this.setState({ showAcknowledgementModal: true });
                  } else {
                    this.setState({ rulesAccepted: false });
                  }
                }}
              >
                <input type="checkbox" checked={rulesAccepted} onChange={() => {}} />
                <span className="mze-check__box">
                  {rulesAccepted && <Check size={12} strokeWidth={3} />}
                </span>
                <span className="mze-check__text">
                  {i18n.t('acknowledge_auction_rules') || 'I acknowledge the auction rules'}
                  <span className="mze-field__required">*</span>
                </span>
              </label>

              {rulesAccepted && (
                <div className="mze-rules-actions">
                  <button
                    type="button"
                    className="mze-rules-btn"
                    onClick={this.handleAcknowledgementView}
                    disabled={isAcknowledgementLoading}
                  >
                    {isAcknowledgementLoading ? (
                      <Loader2 size={16} className="mze-submit__spinner" />
                    ) : (
                      <Eye size={14} />
                    )}
                    <span>{i18n.t('preview') || 'Preview'}</span>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className="mze-submit"
              onClick={this.handleEnrollSubmit}
              disabled={this.isSubmitDisabled}
            >
              {isEnrolled ? (
                <>
                  <Loader size={16} className="mze-submit__spinner" /> Loading...
                </>
              ) : isOnline && hasEnrollAmount ? (
                <>
                  {`${i18n.t('pay')} ${subtotal.toFixed(3)}`}
                  <OMR size={12} strokeWidth={2} color={'#ffffff'} />
                </>
              ) : (
                i18n.t('save_continue')
              )}
            </button>
          </div>
        )}

        {(isSubmitting || isPaymentRedirecting) && (
          <div className="mze-loader">
            <Loader size={24} className="mze-loader__icon" />
          </div>
        )}

        {!isSubmitting && showSuccessMessage && (
          <div className="mze-alert mze-alert--success mze-alert--compact">
            <CheckCircle size={18} className="mze-alert__icon" />
            <div className="mze-alert__content">
              Enrollment completed! Go to{' '}
              <Link
                to={`/auction-details/${auctionId}`}
                className="mze-alert__link mze-alert__link--success"
              >
                Auction
              </Link>{' '}
              page and start bidding.
            </div>
          </div>
        )}
      </>
    );
  }

  // ─── Add this new method ───────────────────────────────────
  renderAcknowledgementModal() {
    const { showAcknowledgementModal, auctionDetails } = this.state;
    const clientName = auctionDetails?.organizationDetails?.organization_name || '—';
    const groupName = this.localizedGroupName || '—';

    const terms = [i18n.t('ack_term_1'), i18n.t('ack_term_2'), i18n.t('ack_term_3')];

    const footer = (
      <div className="mze-ack-footer">
        <button
          type="button"
          className="modal-btn btn-cancel"
          onClick={() => this.setState({ showAcknowledgementModal: false, rulesAccepted: false })}
        >
          <X size={14} />
          <span>{i18n.t('decline')}</span>
        </button>
        <button
          type="button"
          className="modal-btn btn-confirm"
          onClick={() => this.setState({ showAcknowledgementModal: false, rulesAccepted: true })}
        >
          <Check size={14} />
          <span>{i18n.t('accept_confirm')}</span>
        </button>
      </div>
    );

    return (
      <CustomModal
        isOpen={showAcknowledgementModal}
        header={i18n.t('auction_agreement_acknowledgement')}
        headerIcon={<FileText size={18} />}
        footer={footer}
        modalClassName="mze-ack-modal"
        closeBtn={false}
      >
        {/* ── Enrollment confirmation ── */}
        <div className="mze-ack-block">
          <div className="mze-ack-block__head">
            <FileText size={13} />
            <span>{i18n.t('enrollment_confirmation')}</span>
          </div>
          <div className="mze-ack-block__body">
            <p className="mze-ack-intro">
              {i18n.t('ack_intro') ||
                'I hereby confirm that I have successfully enrolled for the following auction:'}
            </p>
            <div className="mze-ack-table">
              <div className="mze-ack-row">
                <span className="mze-ack-row__key">{i18n.t('client_name')}</span>
                <span className="mze-ack-row__val">{clientName}</span>
              </div>
              <div className="mze-ack-row">
                <span className="mze-ack-row__key">{i18n.t('group_name')}</span>
                <span className="mze-ack-row__val">{groupName}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Terms ── */}
        <div className="mze-ack-block">
          <div className="mze-ack-block__head">
            <FileText size={13} />
            <span>{i18n.t('acknowledgement_of_terms')}</span>
          </div>
          <div className="mze-ack-block__body mze-ack-block__body--terms">
            {terms.map((term, idx) => (
              <div key={idx} className="mze-ack-term">
                <div className="mze-ack-term__num">{idx + 1}</div>
                <p className="mze-ack-term__text">{term}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Legal notice ── */}
        <div className="mze-ack-notice">
          <AlertCircle size={14} />
          <p>{i18n.t('ack_legal_notice')}</p>
        </div>
      </CustomModal>
    );
  }
  renderPaymentPanel() {
    const { isProfileComplete, paymentType } = this.state;
    if (!isProfileComplete) return null;

    return (
      <div className="mze-panel">
        <div className="mze-panel__header">
          <Wallet size={16} />
          <span>{i18n.t('pay_type') || 'Payment Method'}</span>
        </div>
        <div className="mze-panel__body">
          {this.renderPaymentTypeTabs()}
          {paymentType === 'offline' && this.renderOfflinePaymentForm()}
          {paymentType === 'online' && this.renderOnlinePaymentForm()}
          {paymentType === 'wallet' && this.renderWalletPaymentForm()}

          {this.renderSubmitSection()}
        </div>
      </div>
    );
  }

  renderBankDetailsPanel() {
    const { showBankPanel, copiedField, isGeneratingImage } = this.state;
    const bankAccount = this.getBankAccount();
    const isFallback = this.isUsingFallbackBankAccount();

    return (
      <div className="mze-panel mze-panel--collapsible">
        <button
          className="mze-panel__toggle"
          onClick={() => this.setState({ showBankPanel: !showBankPanel })}
        >
          <span className="mze-panel__toggle-left">
            <Building2 size={16} />
            {i18n.t('bank_details')}
          </span>
          {showBankPanel ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showBankPanel && (
          <div className="mze-panel__body">
            {bankAccount ? (
              <>
                <div className="mze-bank-notice">
                  <AlertCircle size={14} />
                  <p>
                    {i18n.t('verify_bank_details_note') ||
                      'Please verify these details carefully before transferring the amount. Transfers to the wrong account cannot be reversed.'}
                  </p>
                </div>

                {isFallback && (
                  <div className="mze-bank-fallback-note">
                    <Info size={13} />
                    <span>
                      {i18n.t('using_default_mzadcom_account') ||
                        'No dedicated enrollment account is configured for this auction — showing the default Mzadcom account instead.'}
                    </span>
                  </div>
                )}

                <div className="mze-bank-card mze-bank-card--detailed">
                  {BANK_DETAIL_FIELDS.map((field) => {
                    const value = bankAccount[field.key];
                    if (!value) return null;
                    const isCopied = copiedField === field.key;

                    return (
                      <div className="mze-bank-row" key={field.key}>
                        <div className="mze-bank-row__meta">
                          <span className="mze-bank-row__label">{field.label}</span>
                          <span className="mze-bank-row__value">{value}</span>
                        </div>
                        <button
                          type="button"
                          className={`mze-bank-row__copy ${isCopied ? 'mze-bank-row__copy--copied' : ''}`}
                          onClick={() => this.handleCopyField(field.key, value)}
                          title={i18n.t('copy') || 'Copy'}
                        >
                          {isCopied ? <Check size={14} /> : <Copy size={14} />}
                        </button>
                      </div>
                    );
                  })}
                </div>

                <div className="mze-bank-actions">
                  <button
                    type="button"
                    className="mze-bank-action-btn mze-bank-action-btn--download"
                    onClick={this.handleDownloadBankCard}
                    disabled={isGeneratingImage}
                  >
                    {isGeneratingImage ? (
                      <Loader2 size={16} className="spin" />
                    ) : (
                      <Download size={16} />
                    )}
                    {isGeneratingImage
                      ? i18n.t('generating') || 'Generating...'
                      : i18n.t('download_details') || 'Download Details'}
                  </button>
                </div>
              </>
            ) : (
              <div className="mze-bank-card mze-bank-card--empty">
                <span className="mze-bank-empty">
                  {i18n.t('bank_details_unavailable') ||
                    'Bank details are not configured yet. Please contact admin.'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
  // ─── Skeleton Loading ────────────────────────────────────────
  renderSkeleton() {
    return (
      <div className="mze-page">
        <div className="mze-skel-banner">
          <div className="mze-skel-shimmer mze-skel-banner__title" />
          <div className="mze-skel-shimmer mze-skel-banner__subtitle" />
        </div>

        <div className="mze-container">
          <div className="mze-grid">
            <div className="mze-left">
              {/* Auction info card */}
              <div className="mze-skel-card">
                <div className="mze-skel-row">
                  <div className="mze-skel-shimmer mze-skel-badge" />
                  <div className="mze-skel-shimmer mze-skel-title-lg" />
                  <div className="mze-skel-shimmer mze-skel-line-sm" />
                </div>
                <div className="mze-skel-shimmer mze-skel-amount" />
              </div>

              {/* Payment panel */}
              <div className="mze-skel-card">
                <div className="mze-skel-shimmer mze-skel-heading" />
                <div className="mze-skel-tabs">
                  <div className="mze-skel-shimmer mze-skel-tab" />
                  <div className="mze-skel-shimmer mze-skel-tab" />
                  <div className="mze-skel-shimmer mze-skel-tab" />
                </div>
                <div className="mze-skel-shimmer mze-skel-upload" />
                <div className="mze-skel-fields-grid">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div className="mze-skel-field" key={i}>
                      <div className="mze-skel-shimmer mze-skel-label" />
                      <div className="mze-skel-shimmer mze-skel-input" />
                    </div>
                  ))}
                </div>
                <div className="mze-skel-shimmer mze-skel-btn" />
              </div>
            </div>

            <div className="mze-right">
              <div className="mze-skel-card">
                <div className="mze-skel-shimmer mze-skel-heading" />
                {Array.from({ length: 5 }).map((_, i) => (
                  <div className="mze-skel-bankrow" key={i}>
                    <div className="mze-skel-shimmer mze-skel-line-sm" />
                    <div className="mze-skel-shimmer mze-skel-line-md" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // ─── Main Render ───────────────────────────────────────────

  render() {
    const { isPageLoading } = this.state;

    if (isPageLoading) {
      return this.renderSkeleton();
    }
    const { redirectData } = this.state;
    if (redirectData) {
      return (
        <SmartPayRedirectForm
          actionUrl={redirectData.actionUrl}
          accessCode={redirectData.accessCode}
          encRequest={redirectData.encRequest}
        />
      );
    }

    return (
      <React.Fragment>
        <div className="mze-page">
          <HeaderBanner
            title={i18n.t('auction_enrollment_payments')}
            subtitle={i18n.t('manage_and_view_enrollment_payments')}
          />
          <div className="mze-container">
            <div className="mze-grid">
              <div className="mze-left">
                {this.renderAuctionInfoCard()}
                {this.renderAlerts()}
                {this.renderPaymentPanel()}
              </div>
              <div className="mze-right">{this.renderBankDetailsPanel()}</div>
            </div>
          </div>
        </div>
        {this.renderAcknowledgementModal()}
      </React.Fragment>
    );
  }
}
export default withRouter(AuctionEnrollment);
